import { useState, useEffect, useCallback } from 'react';
import { useAccount } from 'wagmi';

interface PoolPrice {
  yesPrice: number;
  noPrice: number;
  lastUpdated: number;
}

interface RealTimeOddsResult {
  yesPrice: number;
  noPrice: number;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
}

/**
 * Hook to fetch real-time odds from PancakeSwap V4 pool positions
 * Replaces random odds with actual pool prices
 */
export function useRealTimeOdds(presaleId: string | undefined): RealTimeOddsResult {
  const [odds, setOdds] = useState<PoolPrice>({
    yesPrice: 50,
    noPrice: 50,
    lastUpdated: Date.now()
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { address } = useAccount();

  const fetchPoolPrices = useCallback(async () => {
    if (!presaleId) {
      setError('Missing presale ID');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First, get the pool information from the subgraph
      const poolInfo = await fetchPoolInfoFromSubgraph(presaleId);
      if (!poolInfo) {
        throw new Error('Pool information not found');
      }
      
      // Fetch pool prices from PancakeSwap V4 using position IDs
      const prices = await fetchPoolPricesFromPCSV4(poolInfo);
      
      setOdds({
        yesPrice: prices.yesPrice,
        noPrice: prices.noPrice,
        lastUpdated: Date.now()
      });
    } catch (err) {
      console.error('Error fetching real-time odds:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch odds');
      
      // Fallback to subgraph odds if available
      try {
        const fallbackOdds = await fetchFallbackOdds(presaleId);
        if (fallbackOdds) {
          setOdds({
            yesPrice: fallbackOdds.yesPrice,
            noPrice: fallbackOdds.noPrice,
            lastUpdated: Date.now()
          });
        }
      } catch (fallbackErr) {
        console.error('Fallback odds fetch failed:', fallbackErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, [presaleId]);

  // Fetch prices on mount and set up polling
  useEffect(() => {
    fetchPoolPrices();
    
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(fetchPoolPrices, 30000);
    
    return () => clearInterval(interval);
  }, [fetchPoolPrices]);

  return {
    yesPrice: odds.yesPrice,
    noPrice: odds.noPrice,
    isLoading,
    error,
    lastUpdated: odds.lastUpdated
  };
}

/**
 * Fetch pool information from subgraph
 */
async function fetchPoolInfoFromSubgraph(presaleId: string): Promise<{
  yesPositionId: string;
  noPositionId: string;
  yesToken: string;
  noToken: string;
} | null> {
  try {
    const SUBGRAPH_URL = (import.meta as any).env?.VITE_SUBGRAPH_URL;
    if (!SUBGRAPH_URL) {
      throw new Error('Subgraph URL not configured');
    }

    const query = `
      query GetPresalePoolInfo($presaleId: Bytes!) {
        presale(id: $presaleId) {
          id
          yesToken
          noToken
          poolKey {
            yesPositionId
            noPositionId
          }
        }
      }
    `;

    const response = await fetch(SUBGRAPH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        variables: { presaleId }
      })
    });

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(data.errors[0].message);
    }

    const presale = data.data?.presale;
    if (!presale || !presale.poolKey) {
      return null;
    }

    return {
      yesPositionId: presale.poolKey.yesPositionId,
      noPositionId: presale.poolKey.noPositionId,
      yesToken: presale.yesToken,
      noToken: presale.noToken
    };
  } catch (error) {
    console.error('Error fetching pool info from subgraph:', error);
    return null;
  }
}

/**
 * Fetch actual pool prices from SwapperV4FromPosition contract
 */
async function fetchPoolPricesFromPCSV4(poolInfo: {
  yesPositionId: string;
  noPositionId: string;
  yesToken: string;
  noToken: string;
}): Promise<{ yesPrice: number; noPrice: number }> {
  try {
    // Import the contract ABI and address
    const { SWAPPER_V4_FROM_POSITION_ADDRESS, SWAPPER_V4_FROM_POSITION_ABI } = await import('@/lib/contracts');
    
    // Create contract instance using Vite RPC configuration
    const { createPublicClient, http } = await import('viem');
    const { bsc } = await import('viem/chains');
    
    const RPC_URL = (import.meta as any).env?.VITE_BASE_RPC_URL || 'https://bsc-mainnet.infura.io/v3/d2ea31ea15274181a6181dc2e99cd4d6';
    const publicClient = createPublicClient({
      chain: bsc,
      transport: http(RPC_URL)
    });
    
    // First, get the actual pool currencies from the position (like in test script)
    console.log('Getting pool currencies from position...');
    const poolKey = await publicClient.readContract({
      address: SWAPPER_V4_FROM_POSITION_ADDRESS as `0x${string}`,
      abi: SWAPPER_V4_FROM_POSITION_ABI,
      functionName: 'getPoolKeyFromPosition',
      args: [BigInt(poolInfo.yesPositionId)]
    });
    
    const actualCurrency0 = poolKey.currency0;
    const actualCurrency1 = poolKey.currency1;
    
    console.log('Actual pool currencies:', {
      currency0: actualCurrency0,
      currency1: actualCurrency1,
      subgraphYesToken: poolInfo.yesToken,
      subgraphNoToken: poolInfo.noToken
    });
    
    // Determine which currency is YES vs NO based on the subgraph data
    // We need to match the subgraph tokens to the actual pool currencies
    let yesCurrency: string;
    let noCurrency: string;
    
    if (poolInfo.yesToken.toLowerCase() === actualCurrency0.toLowerCase()) {
      // YES token matches currency0
      yesCurrency = actualCurrency0;
      noCurrency = actualCurrency1;
    } else if (poolInfo.yesToken.toLowerCase() === actualCurrency1.toLowerCase()) {
      // YES token matches currency1
      yesCurrency = actualCurrency1;
      noCurrency = actualCurrency0;
    } else {
      // Fallback: assume currency1 is YES (common pattern)
      yesCurrency = actualCurrency1;
      noCurrency = actualCurrency0;
    }
    
    console.log('Currency mapping:', {
      yesCurrency,
      noCurrency,
      subgraphYesToken: poolInfo.yesToken,
      subgraphNoToken: poolInfo.noToken
    });
    
    // Get detailed market pricing using correctly mapped currencies
    const [yesPrice, noPrice, priceRatio, sqrtPriceX96] = await publicClient.readContract({
      address: SWAPPER_V4_FROM_POSITION_ADDRESS as `0x${string}`,
      abi: SWAPPER_V4_FROM_POSITION_ABI,
      functionName: 'getMarketPricing',
      args: [
        BigInt(poolInfo.yesPositionId), // Use YES position ID
        yesCurrency as `0x${string}`,    // Correctly mapped YES currency
        noCurrency as `0x${string}`      // Correctly mapped NO currency
      ]
    });
    
    // Convert from probability format (0-1e18) to percentage (0-100)
    const yesPricePercent = Number(yesPrice) / 1e16; // Convert from 1e18 to percentage
    const noPricePercent = Number(noPrice) / 1e16;   // Convert from 1e18 to percentage
    
    const priceRatioValue = Number(priceRatio) / 1e18;
    
    console.log('Real-time pricing data:', {
      yesPrice: yesPricePercent.toFixed(2) + '%',
      noPrice: noPricePercent.toFixed(2) + '%',
      priceRatio: priceRatioValue,
      priceRatioInterpretation: `YES price = $${priceRatioValue.toFixed(6)} (direct probability)`,
      sqrtPriceX96: sqrtPriceX96.toString()
    });
    
    return {
      yesPrice: Math.round(yesPricePercent * 100) / 100, // Round to 2 decimal places
      noPrice: Math.round(noPricePercent * 100) / 100    // Round to 2 decimal places
    };
  } catch (error) {
    console.error('Error fetching pool prices from SwapperV4FromPosition:', error);
    throw error;
  }
}

/**
 * Fallback to subgraph odds if real-time fetch fails
 */
async function fetchFallbackOdds(presaleId: string): Promise<{ yesPrice: number; noPrice: number } | null> {
  try {
    const SUBGRAPH_URL = (import.meta as any).env?.VITE_SUBGRAPH_URL || '';
    if (!SUBGRAPH_URL) {
      throw new Error('Subgraph URL not configured');
    }

    const query = `
      query GetPresaleOdds($id: String!) {
        presale(id: $id) {
          yesOdds
          noOdds
        }
      }
    `;

    const response = await fetch(SUBGRAPH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: { id: presaleId }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const presale = data.data?.presale;
    
    if (presale && presale.yesOdds && presale.noOdds) {
      return {
        yesPrice: parseInt(presale.yesOdds),
        noPrice: parseInt(presale.noOdds)
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching fallback odds:', error);
    return null;
  }
}

/**
 * Hook for charging widget with real-time odds
 */
export function useChargingWidget(presaleId: string | undefined) {
  const { yesPrice, noPrice, isLoading, error, lastUpdated } = useRealTimeOdds(presaleId);
  
  const isCharging = isLoading;
  const chargingProgress = isLoading ? Math.min(100, (Date.now() - lastUpdated) / 1000 * 10) : 100;
  
  return {
    yesPrice,
    noPrice,
    isCharging,
    chargingProgress,
    error,
    lastUpdated
  };
}
